import * as React from 'react';
import { Alert, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import {
    FEED_HOT_MATCH_MIN_BETS_KEY, getDashboardAppSetting, putDashboardDecimalSetting,
} from '../../Api';

/** Used by the API when the setting row is missing (it seeds it on start). */
const DEFAULT_MIN_BETS = 500;

/**
 * Hot matches (docs/feed-configuration.md §2): a match's feed card (TEAM_MATCH) reaches players who favour its sport —
 * or every player without favourite sports — once it has at least this many bets. The API re-reads it within a minute.
 */
export default function FeedHotMatchSettingCard() {
    const [value, setValue] = React.useState('');
    const [stored, setStored] = React.useState<number | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            const row = await getDashboardAppSetting(FEED_HOT_MATCH_MIN_BETS_KEY);
            if (cancelled) return;
            setLoading(false);
            const current = row?.decimalValue ?? null;
            setStored(current);
            setValue(String(current ?? DEFAULT_MIN_BETS));
        })();
        return () => { cancelled = true; };
    }, []);

    const onSave = async () => {
        setNotice(null);
        const parsed = Number(value.trim());
        if (!Number.isInteger(parsed) || parsed < 1) {
            setError('Enter a whole number of bets, 1 or more.');
            return;
        }
        setSaving(true);
        const result = await putDashboardDecimalSetting(FEED_HOT_MATCH_MIN_BETS_KEY, parsed);
        setSaving(false);
        if (result.error != null) {
            setError(`Failed to save: ${result.error}`);
            return;
        }
        setError(null);
        setStored(result.data?.decimalValue ?? parsed);
        setNotice('Saved. Feeds use the new threshold within a minute.');
    };

    return (
        <Card variant="outlined" sx={{ maxWidth: 560 }}>
            <CardContent>
                <Stack gap={1.5}>
                    <Typography variant="subtitle1">Hot matches</Typography>
                    <Typography variant="body2" color="text.secondary">
                        A match becomes a “Hot match” in the feed once it has at least this many bets: shown to players
                        who favour its sport, and to players with no favourite sports at all. Key: {FEED_HOT_MATCH_MIN_BETS_KEY}.
                    </Typography>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
                    {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
                    <Stack direction="row" gap={2} alignItems="flex-start">
                        <TextField
                            label="Minimum bets"
                            type="number"
                            size="small"
                            value={value}
                            disabled={loading}
                            onChange={e => setValue(e.target.value)}
                            inputProps={{ min: 1, step: 1 }}
                            helperText={stored == null && !loading
                                ? `Not saved yet — the default (${DEFAULT_MIN_BETS}) applies.`
                                : ' '}
                        />
                        <Button variant="contained" onClick={onSave} disabled={saving || loading}>
                            {saving ? 'Saving…' : 'Save'}
                        </Button>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}
